// Multi-tenant model.
//  • System Admin (account.system === true, no orgId): the iBetterAgent operator.
//    Provisions organizations, their seats, and each org's first admin.
//  • Organization Admin (account.admin === true, has orgId): manages that org's
//    agents within the org's seat limit.
//  • Agent (has orgId): a regular user inside an org.
//
// Each account has a globally-unique `id`, which is also its data scope
// (`ira_contacts__<id>`, …). Usernames are unique only within an organization.

import { scopedKey } from './scope';

const DATA_BASES = [
  'ira_contacts', 'ira_properties', 'ira_assignments',
  'ira_custom_templates', 'ira_templates', 'ira_run_history',
];

let idSeq = 0;
function genId(prefix) {
  idSeq += 1;
  return `${prefix}_${Date.now().toString(36)}${idSeq.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function loadOrgs() {
  try { return JSON.parse(localStorage.getItem('ira_orgs') || '[]'); } catch { return []; }
}
export function saveOrgs(orgs) { localStorage.setItem('ira_orgs', JSON.stringify(orgs)); }

export function orgById(orgs, id) { return (orgs || []).find(o => o.id === id) || null; }

// Ensure exactly one "System Org" (iBetterAgent direct) per supported MLS. Direct
// self-signups join the System Org matching the MLS they pick. System Orgs never
// expire and have unlimited seats — instead each member has an individual expiry.
// Returns { orgs, changed }.
export function ensureSystemOrgs(orgs, mlsCatalog) {
  const list = Array.isArray(orgs) ? [...orgs] : [];
  let changed = false;
  for (const m of (mlsCatalog || [])) {
    if (!list.some(o => o.systemOrg && o.mlsId === m.id)) {
      list.push({ id: `org_sys_${m.id}`, name: `${m.name} — iBetterAgent`, systemOrg: true, mlsId: m.id, seats: 0, trialSeats: 0, trialDays: 0, disabled: false, createdAt: new Date().toISOString() });
      changed = true;
    }
  }
  return { orgs: list, changed };
}
export function systemOrgForMls(orgs, mlsId) {
  return (orgs || []).find(o => o.systemOrg && o.mlsId === mlsId) || null;
}

// The data scope that holds the system-level (iBetterAgent) reminders: the first
// system-admin account. Public system reminders cascade to new orgs and to
// new independent (org-less) users.
export function systemScope(accounts) {
  const a = (accounts || []).find(x => x.system);
  return a ? a.id : null;
}

// Agents (non-admin, non-system) in an org. Admins don't consume a seat.
export function orgAgents(accounts, orgId) {
  return (accounts || []).filter(a => a.orgId === orgId && !a.admin && !a.system);
}
export function orgMembers(accounts, orgId) {
  return (accounts || []).filter(a => a.orgId === orgId && !a.system);
}
// "Used" seats = active (non-disabled) agents PLUS any extra admins beyond the
// first. The org's first admin is FREE; every additional active admin consumes a
// seat. Disabling a member frees its seat.
export function seatsUsed(accounts, orgId) {
  const activeAgents = orgAgents(accounts, orgId).filter(a => !a.disabled).length;
  const activeAdmins = (accounts || []).filter(a => a.orgId === orgId && a.admin && !a.system && !a.disabled).length;
  const extraAdmins = Math.max(0, activeAdmins - 1);
  return activeAgents + extraAdmins;
}

// ── Org-level trial → paid lifecycle ─────────────────────────────────────────
// An org is provisioned on a trial: `trialSeats` seats free for `trialDays` days
// (from org.createdAt). When the trial ends the org runs on its purchased `seats`
// (X) — which is 0 until the System Admin sets it ("they signed up"). If the trial
// ends with 0 paid seats the org is LOCKED (members blocked) until seats are set.
const DAY_MS = 86400000;
export function orgTrialEndsAt(org) {
  const d = org && org.trialDays ? Number(org.trialDays) : 0;
  if (!d || !org || !org.createdAt) return null;
  const start = new Date(org.createdAt);
  if (isNaN(start.getTime())) return null;
  return new Date(start.getTime() + d * DAY_MS);
}
export function orgInTrial(org, now = new Date()) {
  const end = orgTrialEndsAt(org);
  return !!end && now < end;
}
export function orgTrialDaysLeft(org, now = new Date()) {
  const end = orgTrialEndsAt(org);
  return end ? Math.max(0, Math.ceil((end - now) / DAY_MS)) : null;
}
// The seat limit usable right now: unlimited for System Orgs (members are gated
// individually); otherwise purchased seats — and, while in the trial window, at
// least the trial allotment. So buying seats (e.g. setting Paid) takes effect
// immediately even mid-trial, while a trial with no purchase still grants its seats.
export function orgEffectiveSeats(org, now = new Date()) {
  if (!org) return 0;
  if (org.systemOrg) return Infinity;
  const paid = Number(org.seats) || 0;
  if (orgInTrial(org, now)) return Math.max(Number(org.trialSeats) || 0, paid);
  return paid;
}

// ── Per-user expiry (System Org direct members; iBetterAgent contracts each one) ──
const USER_DAY_MS = 86400000;
// True if a paid subscription is set and still current — a paid agent never "expires".
export function userPaid(account, now = new Date()) {
  if (!account || !account.paidUntil) return false;
  const d = new Date(account.paidUntil);
  return !isNaN(d.getTime()) && now < d;
}
export function userExpired(account, now = new Date()) {
  if (!account || !account.expiresAt) return false;
  if (userPaid(account, now)) return false;   // active paid subscriber never expires
  const d = new Date(account.expiresAt);
  return !isNaN(d.getTime()) && now > d;
}
export function userDaysLeft(account, now = new Date()) {
  if (!account || !account.expiresAt) return null;
  const d = new Date(account.expiresAt);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d - now) / USER_DAY_MS));
}
// True if the org holds a paid subscription that is still current. Seats govern HOW
// MANY agents an org may have; the subscription date (expiresAt) governs UNTIL WHEN
// they're all active. A current subscription keeps the whole org alive regardless of
// the trial window — a lapsed one blocks every member (operator sets it after they pay).
export function orgSubscribed(org, now = new Date()) {
  if (!org || !org.expiresAt) return false;
  const d = new Date(org.expiresAt);
  return !isNaN(d.getTime()) && now < d;
}
// Why an org's access is blocked: 'suspended' (manually), 'expired' (subscription
// lapsed, or trial over with no purchased seats), or null (usable). Members of a
// blocked org are locked out.
export function orgBlockedReason(org, now = new Date()) {
  if (!org) return null;
  if (org.disabled) return 'suspended';
  if (org.expiresAt) return orgSubscribed(org, now) ? null : 'expired';   // subscription is authoritative once set
  if (org.trialDays && !orgInTrial(org, now) && (Number(org.seats) || 0) <= 0) return 'expired';
  return null;
}
// True when a usable org has MORE active members than its current seat limit (e.g.
// it converted to fewer paid seats than trial agents, or the sysadmin reduced seats).
// Reminder sending is paused org-wide until the admin frees seats or buys more — the
// admin chooses who to disable; the system never auto-removes anyone.
export function orgOverCapacity(accounts, org, now = new Date()) {
  if (!org || orgBlockedReason(org, now)) return false;
  return seatsUsed(accounts, org.id) > orgEffectiveSeats(org, now);
}

// Why an account's reminder processing is paused: 'self' (the agent paused their
// own), 'org' (their organization paused all reminders, e.g. maintenance), or null
// (active — the default). Both default to ON unless explicitly suspended.
export function reminderSuspendReason(account, orgs) {
  if (!account) return null;
  if (account.remindersSuspended) return 'self';
  if (account.orgId) {
    const org = (orgs || []).find(o => o.id === account.orgId);
    if (org && org.remindersSuspended) return 'org';
  }
  return null;
}

// ── Process-control hierarchy: System → MLS → Org → Agent ────────────────────
// System & MLS are owned by iBetterAgent (they can launch AND stop processing);
// orgs/agents can only suspend their own. An MLS's explicit process state OVERRIDES
// System; when unset, the MLS inherits System. So if an MLS is stopped, the System
// process won't run on it. An MLS is suspend-only (like an org): it can pause its own
// processing but cannot override a System pause — so it's stopped if EITHER the MLS
// paused itself OR the System is paused (most-restrictive wins).
export function mlsProcessSuspended(mls, settings) {
  return !!(mls && mls.processSuspended) || !!(settings && settings.processSuspended);
}

// Effective trial days for new sign-ups: MLS override wins; falls back to System.
export function effectiveTrialDays(mls, settings) {
  if (mls && mls.signupTrialDays != null) return Number(mls.signupTrialDays);
  return (settings && settings.signupTrialDays != null) ? Number(settings.signupTrialDays) : 30;
}

// Why an account's reminder processing is halted, most-specific first:
// 'self' | 'org' | 'mls' (the account's MLS explicitly stopped) | 'system'
// (the System is paused and the MLS didn't override) | null (active).
export function processHaltReason(account, orgs, mlsList, settings) {
  if (!account) return null;
  if (account.remindersSuspended) return 'self';
  const org = account.orgId ? (orgs || []).find(o => o.id === account.orgId) : null;
  if (org && org.remindersSuspended) return 'org';
  const mlsId = org ? org.mlsId : (account.mls || null);
  const mls = mlsId ? (mlsList || []).find(m => m.id === mlsId) : null;
  if (mls && mls.processSuspended) return 'mls';
  if (settings && settings.processSuspended) return 'system';
  return null;
}

export { genId };

// One-time migration to the multi-tenant model: wrap existing accounts/data in a
// default organization, give every account a stable id (and migrate its data
// keys from `__<username>` to `__<id>`), and bootstrap a System Admin account.
export function ensureTenancy(accounts) {
  let orgs = loadOrgs();

  if (localStorage.getItem('ira_tenancy_v1')) {
    // Defensive: make sure ids / a system admin still exist.
    let changed = false;
    const fixed = accounts.map(a => {
      if (a.id) return a;
      changed = true;
      return { ...a, id: genId('acc') };
    });
    let out = fixed;
    if (!fixed.some(a => a.system)) {
      out = [...fixed, { id: genId('acc'), username: 'sysadmin', password: 'sysadmin', system: true, alias: 'System Admin' }];
      changed = true;
    }
    if (changed) localStorage.setItem('ira_accounts', JSON.stringify(out));
    return { accounts: out, orgs };
  }

  // ── First-time migration ──
  const orgId = 'org_default';
  if (!orgs.length) {
    orgs = [{ id: orgId, name: 'My Organization', seats: 50, disabled: false, createdAt: new Date().toISOString() }];
  }
  const defaultOrgId = orgs[0].id;

  const migrated = (accounts || []).map(a => {
    const id = a.id || genId('acc');
    return a.system
      ? { ...a, id, orgId: undefined }
      : { ...a, id, orgId: a.orgId || defaultOrgId };
  });

  // Move each account's data + its org's MLS catalog to id-/org-scoped keys.
  migrated.forEach(a => {
    if (a.system) return;
    DATA_BASES.forEach(base => {
      const oldKey = scopedKey(base, a.username);
      const newKey = scopedKey(base, a.id);
      const val = localStorage.getItem(oldKey);
      if (val != null && localStorage.getItem(newKey) == null) localStorage.setItem(newKey, val);
    });
  });
  // The previously-global MLS list becomes the default org's MLS.
  const legacyMls = localStorage.getItem('ira_mls');
  if (legacyMls != null && localStorage.getItem(scopedKey('ira_mls', defaultOrgId)) == null) {
    localStorage.setItem(scopedKey('ira_mls', defaultOrgId), legacyMls);
  }

  let withSystem = migrated;
  if (!withSystem.some(a => a.system)) {
    withSystem = [...withSystem, { id: genId('acc'), username: 'sysadmin', password: 'sysadmin', system: true, alias: 'System Admin' }];
  }

  saveOrgs(orgs);
  localStorage.setItem('ira_accounts', JSON.stringify(withSystem));
  localStorage.setItem('ira_tenancy_v1', '1');
  return { accounts: withSystem, orgs };
}
